import { useEffect, useState } from "react";
import { Search, Sun, Moon, LogOut, Cloud, User as UserIcon, Menu, HelpCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useNavigate } from "react-router-dom";
import { SidebarContent } from "@/components/Sidebar";
import { useTour } from "@/lib/tour";

export default function Topbar({ onSearch }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { startTour } = useTour();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => onSearch?.(q), 250);
    return () => clearTimeout(t);
  }, [q, onSearch]);

  const initials = (user?.name || user?.email || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <header
      data-testid="topbar"
      className="h-16 border-b border-border bg-card/70 backdrop-blur-md sticky top-0 z-30 flex items-center gap-2 md:gap-4 px-3 md:px-8"
    >
      {/* Mobile hamburger */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            data-testid="mobile-menu-btn"
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-[280px] p-0 bg-card flex flex-col"
          data-testid="mobile-sidebar"
        >
          <VisuallyHidden>
            <SheetTitle>Mahano OS navigation</SheetTitle>
            <SheetDescription>Access every section of Mahano OS.</SheetDescription>
          </VisuallyHidden>
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 max-w-xl relative min-w-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="global-search"
          placeholder="Search…"
          className="pl-9 h-10 bg-background/60 border-border/80"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground px-3 py-1.5 rounded-lg bg-background/50 border border-border">
        <Cloud className="w-4 h-4" />
        <span>Clear · 22°</span>
      </div>

      <Button
        data-testid="start-tour-btn"
        variant="ghost"
        size="icon"
        onClick={startTour}
        aria-label="Take the product tour"
        title="Take the product tour"
      >
        <HelpCircle className="w-4 h-4" />
      </Button>

      <Button
        data-testid="theme-toggle"
        variant="ghost"
        size="icon"
        onClick={() => setTheme(theme === "light" ? "dark" : "light")}
        aria-label="Toggle theme"
      >
        {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu-trigger" className="h-9 w-9 rounded-full bg-gradient-to-br from-[hsl(var(--studio-primary))] to-[hsl(var(--studio-secondary))] text-primary-foreground text-xs font-semibold flex items-center justify-center shrink-0">
            {initials}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="font-medium">{user?.name}</div>
            <div className="text-xs text-muted-foreground">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="user-menu-settings" onClick={() => navigate("/settings")}>
            <UserIcon className="w-4 h-4 mr-2" /> Profile & Settings
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="user-menu-logout" onClick={logout}>
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
